import React, { useEffect, useState } from 'react'
import styles from './ProfilePage.module.scss'
import { useAuth } from 'contexts/AuthContext'
import { getUserData, patchUserData, patchUserDataForm, getUserAddresses, createUserAddress, updateUserAddress, deleteUserAddress, getUserPaymentMethods, createUserPaymentMethod, updateUserPaymentMethod, deleteUserPaymentMethod, getTitles, getUserDashboard } from 'service/UserService'
import { getFoodOrders } from 'service/FoodService'
import ChangePasswordModal from './ChangePasswordModal/ChangePasswordModal'
import PurchaseList from './PurchaseList/PurchaseList'
import TitleDisplay from './TitleDisplay/TitleDisplay'
import EmailVerificationModal from 'components/EmailVerificationModal/EmailVerificationModal'
import Button from 'components/Button/Button'
import ProfileIcon from 'assets/icons/profile/ProfileIcon'
import HistoryIcon from 'assets/icons/profile/HistoryIcon'
import LocationIcon from 'assets/icons/profile/LocationIcon'
import PaymentIcon from 'assets/icons/profile/PaymentIcon'
import ShoppingIcon from 'assets/icons/profile/ShoppingIcon'
import ShopOrdersList from './ShopOrdersList/ShopOrdersList'
import { ShoppingCartIcon, PizzaIcon, PlaneIcon, ComputerIcon, StarIcon, MoneyIcon } from 'components/CustomIcons'

export default function ProfilePage({ goBack }) {
    const { access, user: authUser, isAuthenticated, fetchWithAuth, fetchProfile, logout } = useAuth()
    const [loading, setLoading] = useState(false)
    // Используем authUser напрямую без локального state
    const user = authUser
    const [error, setError] = useState(null)
    const [openChange, setOpenChange] = useState(false)
    const [openVerification, setOpenVerification] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [formState, setFormState] = useState({ email: user?.email || '', phone: user?.phone || '' })
    const [photoFile, setPhotoFile] = useState(null)
    const [photoPreview, setPhotoPreview] = useState(null)
    const [saving, setSaving] = useState(false)
    
    // Новые состояния для расширенного функционала
    const [activeTab, setActiveTab] = useState('profile')
    const [userHistory, setUserHistory] = useState({
        anime: [],
        movies: [],
        orders: {}, // Изменено на объект для статистики
        activity: []
    })
    const [addresses, setAddresses] = useState([])
    const [paymentMethods, setPaymentMethods] = useState([])
    const [showAddressForm, setShowAddressForm] = useState(false)
    const [showPaymentForm, setShowPaymentForm] = useState(false)
    const [editingAddress, setEditingAddress] = useState(null)
    const [editingPayment, setEditingPayment] = useState(null)
    const [addressForm, setAddressForm] = useState({
        label: '',
        country: '',
        city: '',
        street: '',
        zipCode: '',
        isDefault: false
    })
    const [paymentForm, setPaymentForm] = useState({
        provider: 'card',
        maskedDetails: '',
        isDefault: false
    })
    const [allTitles, setAllTitles] = useState([])
    const [nextTitle, setNextTitle] = useState(null)
    const [dashboardLoading, setDashboardLoading] = useState(false)
    const [foodOrders, setFoodOrders] = useState([])
    const [foodOrdersLoading, setFoodOrdersLoading] = useState(false)
    const [tourBookings, setTourBookings] = useState([])
    const [tourBookingsLoading, setTourBookingsLoading] = useState(false)

    useEffect(() => {
        if (!access) {
            return
        }

        // Загружаем дополнительные данные в фоне
        loadUserHistory()
        loadUserAddresses()
        loadPaymentMethods()
        loadFoodOrders()
        loadTourBookings()
        
        // Загружаем титулы и потом dashboard с использованием титулов
        const loadTitlesAndDashboard = async () => {
            try {
                const titlesResponse = await getTitles()
                const loadedTitles = titlesResponse.data.results || titlesResponse.data || []
                setAllTitles(loadedTitles)
                console.log('📚 Titles loaded:', loadedTitles.length);
                
                // Теперь загружаем dashboard с титулами
                await loadDashboard(loadedTitles)
            } catch (error) {
                console.error('Ошибка загрузки титулов:', error)
            }
        }
        
        loadTitlesAndDashboard()
    }, [access])  // Убираем user из зависимостей, чтобы избежать лишних перезагрузок

    // Дополнительный useEffect для принудительной загрузки dashboard при изменении пользователя
    useEffect(() => {
        if (user && access && allTitles.length > 0) {
            console.log('🔄 User changed, reloading dashboard...');
            loadDashboard(allTitles);
        }
    }, [user?.id, user?.activity_points, user?.title]); // Следим за ключевыми полями пользователя

    // Новые функции для загрузки данных
    const loadUserHistory = async () => {
        if (!access) return
        try {
            // Загрузка истории аниме
            let animeData = { results: [] }
            try {
                const animeResponse = await fetch(`/api/anime/history/`, {
                    headers: { Authorization: `Bearer ${access}` }
                })
                animeData = await animeResponse.json()
            } catch (err) {
                console.warn('Failed to load anime history:', err)
            }
            
            // Загрузка истории фильмов
            let moviesData = { results: [] }
            try {
                const moviesResponse = await fetch(`/api/movie/history/`, {
                    headers: { Authorization: `Bearer ${access}` }
                })
                moviesData = await moviesResponse.json()
            } catch (err) {
                console.warn('Failed to load movies history:', err)
            }
            
            // Загрузка статистики заказов из dashboard
            let dashboardData = { orders_summary: {} }
            try {
                const dashboardResponse = await getUserDashboard()
                dashboardData = dashboardResponse.data
            } catch (err) {
                console.error('Failed to load dashboard:', err)
            }
            
            // Загрузка журнала активности
            let activityData = { results: [] }
            try {
                const activityResponse = await fetch(`/api/account/activity-logs/`, {
                    headers: { Authorization: `Bearer ${access}` }
                })
                activityData = await activityResponse.json()
            } catch (err) {
                console.warn('Failed to load activity logs:', err)
            }

            // Создаем новый объект orders с явным копированием свойств
            const ordersData = {
                shop_orders: dashboardData.orders_summary?.shop_orders ?? 0,
                food_orders: dashboardData.orders_summary?.food_orders ?? 0,
                it_requests: dashboardData.orders_summary?.it_requests ?? 0,
                event_registrations: dashboardData.orders_summary?.event_registrations ?? 0,
                tour_registrations: dashboardData.orders_summary?.tour_registrations ?? 0
            }

            setUserHistory({
                anime: animeData.results || animeData || [],
                movies: moviesData.results || moviesData || [],
                orders: ordersData,
                activity: activityData.results || activityData || []
            })
        } catch (error) {
            console.error('Ошибка загрузки истории:', error)
        }
    }

    const loadUserAddresses = async () => {
        if (!access) return
        try {
            const response = await getUserAddresses()
            setAddresses(response.data.results || response.data || [])
        } catch (error) {
            console.error('Ошибка загрузки адресов:', error)
        }
    }

    const loadPaymentMethods = async () => {
        if (!access) return
        try {
            const response = await getUserPaymentMethods()
            setPaymentMethods(response.data.results || response.data || [])
        } catch (error) {
            console.error('Ошибка загрузки способов оплаты:', error)
        }
    }

    const loadFoodOrders = async () => {
        if (!access) return
        setFoodOrdersLoading(true)
        try {
            const response = await getFoodOrders()
            setFoodOrders(response.data.results || response.data || [])
        } catch (error) {
            console.error('Ошибка загрузки заказов еды:', error)
        } finally {
            setFoodOrdersLoading(false)
        }
    }

    const loadTourBookings = async () => {
        if (!access) return
        setTourBookingsLoading(true)
        try {
            const { getMyTourBookings } = await import('service/TourismService')
            const response = await getMyTourBookings()
            setTourBookings(response.data.results || response.data || [])
        } catch (error) {
            console.error('Ошибка загрузки бронирований туров:', error)
        } finally {
            setTourBookingsLoading(false)
        }
    }

    const handleAddressSave = async () => {
        try {
            let response;
            if (editingAddress) {
                response = await updateUserAddress(editingAddress.id, addressForm)
            } else {
                response = await createUserAddress(addressForm)
            }
            
            if (response.status === 200 || response.status === 201) {
                loadUserAddresses()
                setShowAddressForm(false)
                setEditingAddress(null)
                setAddressForm({
                    label: '',
                    country: '',
                    city: '',
                    street: '',
                    zipCode: '',
                    isDefault: false
                })
            }
        } catch (error) {
            console.error('Ошибка сохранения адреса:', error)
        }
    }

    const handlePaymentSave = async () => {
        try {
            let response;
            if (editingPayment) {
                response = await updateUserPaymentMethod(editingPayment.id, paymentForm)
            } else {
                response = await createUserPaymentMethod(paymentForm)
            }
            
            if (response.status === 200 || response.status === 201) {
                loadPaymentMethods()
                setShowPaymentForm(false)
                setEditingPayment(null)
                setPaymentForm({
                    provider: 'card',
                    maskedDetails: '',
                    isDefault: false
                })
            }
        } catch (error) {
            console.error('Ошибка сохранения способа оплаты:', error)
        }
    }

    const deleteAddress = async (addressId) => {
        if (window.confirm('Удалить этот адрес?')) {
            try {
                await deleteUserAddress(addressId)
                loadUserAddresses()
            } catch (error) {
                console.error('Ошибка удаления адреса:', error)
            }
        }
    }

    const deletePaymentMethod = async (paymentId) => {
        if (window.confirm('Удалить этот способ оплаты?')) {
            try {
                await deleteUserPaymentMethod(paymentId)
                loadPaymentMethods()
            } catch (error) {
                console.error('Ошибка удаления способа оплаты:', error)
            }
        }
    }

    const loadDashboard = async (titles = null) => {
        if (!access || dashboardLoading) return
        setDashboardLoading(true)
        try {
            const response = await getUserDashboard()
            const data = response.data
            console.log('📊 Dashboard data:', data);
            
            if (data.progress_to_next_title) {
                // Используем переданные титулы или загружаем заново
                const titlesToUse = titles || allTitles
                
                // Если список титулов все еще пуст, загружаем их
                if (titlesToUse.length === 0) {
                    console.log('📚 Loading titles for dashboard...');
                    const titlesResponse = await getTitles()
                    const loadedTitles = titlesResponse.data.results || titlesResponse.data || []
                    setAllTitles(loadedTitles)
                    
                    // Находим следующий титул
                    const next = loadedTitles.find(t => t.name === data.progress_to_next_title.next_title)
                    if (next) {
                        console.log('🎯 Next title:', next);
                        setNextTitle(next)
                    }
                } else {
                    // Находим следующий титул в списке
                    const next = titlesToUse.find(t => t.name === data.progress_to_next_title.next_title)
                    if (next) {
                        console.log('🎯 Next title:', next);
                        setNextTitle(next)
                    }
                }
            } else {
                // Если нет прогресса к следующему титулу, пользователь достиг максимального
                setNextTitle(null)
            }
        } catch (error) {
            console.error('Ошибка загрузки dashboard:', error)
            setNextTitle(null)
        } finally {
            setDashboardLoading(false)
        }
    }

    const formatDate = (dateString) => {
        // Parse date in format "DD.MM.YYYY HH:MM"
        const [datePart, timePart] = dateString.split(' ')
        const [day, month, year] = datePart.split('.')
        const [hour, minute] = timePart.split(':')
        const date = new Date(year, month - 1, day, hour, minute)
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getProgressPercent = (current, total) => {
        return total > 0 ? Math.round((current / total) * 100) : 0
    }

    // Убрал проверку isAuthenticated - показываем профиль сразу
    // Данные уже приходят моментально после логина
    return (
        <div className={styles.profilePage}>
            <button onClick={goBack} className={styles.btnModalClose}>Назад</button>
            
            <div className={styles.container}>
                <div className={styles.sidebar}>
                    <div className={styles.sidebarNav}>
                        <button 
                            className={activeTab === 'profile' ? styles.active : ''}
                            onClick={() => setActiveTab('profile')}
                        >
                            <span className={styles.icon}><ProfileIcon size={20} /></span>
                            Профиль
                        </button>
                        <button 
                            className={activeTab === 'history' ? styles.active : ''}
                            onClick={() => setActiveTab('history')}
                        >
                            <span className={styles.icon}><HistoryIcon size={20} /></span>
                            История
                        </button>
                        <button 
                            className={activeTab === 'addresses' ? styles.active : ''}
                            onClick={() => setActiveTab('addresses')}
                        >
                            <span className={styles.icon}><LocationIcon size={20} /></span>
                            Адреса
                        </button>
                        <button 
                            className={activeTab === 'payment' ? styles.active : ''}
                            onClick={() => setActiveTab('payment')}
                        >
                            <span className={styles.icon}><PaymentIcon size={20} /></span>
                            Оплата
                        </button>
                        <button 
                            className={activeTab === 'purchases' ? styles.active : ''}
                            onClick={() => {
                                setActiveTab('purchases')
                                // Принудительно перезагружаем данные при переходе на вкладку
                                if (Object.keys(userHistory.orders).length === 0) {
                                    loadUserHistory()
                                }
                            }}
                        >
                            <span className={styles.icon}><ShoppingIcon size={20} /></span>
                            Покупки
                        </button>
                        <button 
                            className={activeTab === 'shop-orders' ? styles.active : ''}
                            onClick={() => setActiveTab('shop-orders')}
                        >
                            <span className={styles.icon}><ShoppingCartIcon size={20} /></span>
                            Заказы магазина
                        </button>
                        <button 
                            className={activeTab === 'food-orders' ? styles.active : ''}
                            onClick={() => setActiveTab('food-orders')}
                        >
                            <span className={styles.icon}><PizzaIcon size={20} /></span>
                            Заказы еды
                        </button>
                        <button 
                            className={activeTab === 'tour-bookings' ? styles.active : ''}
                            onClick={() => setActiveTab('tour-bookings')}
                        >
                            <span className={styles.icon}><PlaneIcon size={20} /></span>
                            Бронирования туров
                        </button>
                    </div>
                </div>

                <div className={styles.content}>
                    {activeTab === 'profile' && (
                        <div className={styles.profileInfo}>
                            <div className={styles.left_div}>
                                <h2>Профиль</h2>
                                <Button className={styles.btnExit} onClick={async () => {
                                    console.log('🚪 Logout button clicked');
                                    await logout();
                                    // logout уже делает reload, поэтому не нужно дополнительных действий
                                }}>Выйти</Button>
                            </div>

                            {user ? (
                                <div className={styles.profileContent}>
                                    <div className={styles.avatarSection}>
                                        <div className={styles.left_div}>
                                            {photoPreview || user.avatar ? (
                                                <div className={styles.avatar}>
                                                    <img src={photoPreview || user.avatar} alt="avatar" />
                                                </div>
                                            ) : (
                                                <div className={styles.avatarPlaceholder}>
                                                    {user.username?.charAt(0)?.toUpperCase() || user.first_name?.charAt(0)?.toUpperCase() || 'П'}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className={styles.userStats}>
                                            <div className={styles.name}>
                                                {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
                                            </div>
                                            
                                            <div className={styles.statsGrid}>
                                                <div className={styles.stat}>
                                                    <span className={styles.statValue}>{user.activity_points || 0}</span>
                                                    <span className={styles.statLabel}>Очки активности</span>
                                                </div>
                                                <div className={styles.stat}>
                                                    <span className={styles.statValue}>{user.cashback_balance || 0}Сом</span>
                                                    <span className={styles.statLabel}>Баланс кешбека</span>
                                                </div>
                                                <div className={styles.stat}>
                                                    <span className={styles.statValue}>
                                                        {user.is_verified ? 'Да' : 'Нет'}
                                                    </span>
                                                    <span className={styles.statLabel}>Верификация</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Красивое отображение титулов */}
                                    <TitleDisplay user={user} nextTitle={nextTitle} allTitles={allTitles} loading={dashboardLoading} />

                                    {/* Статус верификации */}
                                    {!user.is_verified && (
                                        <div className={styles.verificationBanner}>
                                            <div className={styles.verificationIcon}>⚠️</div>
                                            <div className={styles.verificationContent}>
                                                <h4>Подтвердите ваш email</h4>
                                                <p>Для доступа ко всем функциям платформы необходимо верифицировать email</p>
                                            </div>
                                            <Button 
                                                className={styles.verificationBtn}
                                                onClick={() => setOpenVerification(true)}
                                            >
                                                Пройти верификацию
                                            </Button>
                                        </div>
                                    )}

                                    <div className={styles.profileDetails}>
                                        {!editMode ? (
                                            <div className={styles.detailsView}>
                                                <div className={styles.detailRow}>
                                                    <span className={styles.label}>Email:</span>
                                                    <span className={styles.value}>{user.email}</span>
                                                </div>
                                                <div className={styles.detailRow}>
                                                    <span className={styles.label}>Телефон:</span>
                                                    <span className={styles.value}>{user.phone || 'Не указан'}</span>
                                                </div>
                                                <div className={styles.detailRow}>
                                                    <span className={styles.label}>Дата регистрации:</span>
                                                    <span className={styles.value}>
                                                        {formatDate(user.date_joined)}
                                                    </span>
                                                </div>
                                                
                                                <div className={styles.btnSecondary_div}>
                                                    <Button className={styles.btnSecondary} onClick={() => setOpenChange(true)}>
                                                        Сменить пароль
                                                    </Button>
                                                    <Button className={styles.btnSecondary} onClick={() => {
                                                        setFormState({ email: user?.email || '', phone: user?.phone || '' })
                                                        setPhotoPreview(null)
                                                        setPhotoFile(null)
                                                        setEditMode(true)
                                                    }}>
                                                        Редактировать
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.editForm}>
                                                <div>
                                                    <label className="auth-label">Почта</label>
                                                    <input className="auth-input" value={formState.email} onChange={(e) => setFormState(s => ({ ...s, email: e.target.value }))} />
                                                </div>
                                                <div>
                                                    <label className="auth-label">Телефон</label>
                                                    <input className="auth-input" value={formState.phone} onChange={(e) => setFormState(s => ({ ...s, phone: e.target.value }))} />
                                                </div>
                                                <div>
                                                    <label className="auth-label">Фото профиля</label>
                                                    <div className="avatar-upload">
                                                        {photoPreview && (
                                                            <div className={styles.avatar1}>
                                                                <img src={photoPreview} alt="avatar preview" />
                                                            </div>
                                                        )}
                                                        <label className="avatar-file-label">
                                                            Загрузить фото/GIF
                                                            <input
                                                                type="file"
                                                                accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                                                                className="avatar-file-input"
                                                                onChange={e => {
                                                                    const file = e.target.files && e.target.files[0]
                                                                    setPhotoFile(file || null)
                                                                    if (file) {
                                                                        const reader = new FileReader()
                                                                        reader.onload = ev => setPhotoPreview(ev.target.result)
                                                                        reader.readAsDataURL(file)
                                                                    } else {
                                                                        setPhotoPreview(null)
                                                                    }
                                                                }}
                                                            />
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className={styles.editForm_btn}>
                                                    <Button className={styles.btn} onClick={() => setEditMode(false)}>Отмена</Button>
                                                    <Button className={styles.btnPrimary} onClick={async () => {
                                                        // client validation
                                                        if (formState.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formState.email)) {
                                                            setError('Неверный формат email')
                                                            return
                                                        }
                                                        setSaving(true)
                                                        setError(null)
                                                        try {
                                                            let updatedUser = null;
                                                            
                                                            // decide multipart if photo provided
                                                            if (photoFile) {
                                                                console.log('📤 Uploading photo...');
                                                                const form = new FormData()
                                                                form.append('avatar_upload', photoFile)
                                                                if (formState.phone) form.append('phone', formState.phone)
                                                                if (formState.email) form.append('email', formState.email)
                                                                const res = await patchUserDataForm(form)
                                                                console.log('✅ Photo uploaded successfully:', res.data);
                                                                updatedUser = res.data;
                                                            } else {
                                                                console.log('📤 Updating user data...');
                                                                const payload = {}
                                                                if (formState.email) payload.email = formState.email
                                                                if (formState.phone) payload.phone = formState.phone
                                                                const res = await patchUserData(payload)
                                                                console.log('✅ User data updated successfully:', res.data);
                                                                updatedUser = res.data;
                                                            }
                                                            
                                                            // Обновляем профиль из контекста
                                                            await fetchProfile();
                                                            console.log('✅ Profile refreshed from server');
                                                            
                                                            // Закрываем режим редактирования
                                                            setEditMode(false)
                                                            setPhotoFile(null)
                                                            setPhotoPreview(null)
                                                            
                                                            console.log('✅ Edit mode closed successfully');
                                                        } catch (e) {
                                                            console.error('❌ Error updating profile:', e);
                                                            
                                                            if (e.response && e.response.status === 401) {
                                                                window.dispatchEvent(new CustomEvent('sessionExpired'))
                                                                return
                                                            }
                                                            
                                                            // Показываем ошибку только если реально провалился основной запрос
                                                            const errObj = (e.response && e.response.data) ? e.response.data : { _error: ['Сетевая ошибка'] }
                                                            setError(typeof errObj === 'string' ? errObj : JSON.stringify(errObj))
                                                        } finally {
                                                            setSaving(false)
                                                        }
                                                    }} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</Button>
                                                </div>
                                                {error && <div style={{ color: 'red' }}>{String(error)}</div>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className={styles.historySection}>
                            <h2>История просмотров</h2>
                            <div className={styles.historyTabs}>
                                <button 
                                    className={activeTab === 'anime' ? styles.active : ''}
                                    onClick={() => setActiveTab('anime')}
                                >
                                    Аниме ({userHistory.anime.length})
                                </button>
                                <button 
                                    className={activeTab === 'movies' ? styles.active : ''}
                                    onClick={() => setActiveTab('movies')}
                                >
                                    Фильмы ({userHistory.movies.length})
                                </button>
                                <button 
                                    className={activeTab === 'activity' ? styles.active : ''}
                                    onClick={() => setActiveTab('activity')}
                                >
                                    Активность ({userHistory.activity.length})
                                </button>
                            </div>

                            <div className={styles.historyContent}>
                                {/* История аниме */}
                                {userHistory.anime.map((item) => (
                                    <div key={item.id} className={styles.historyItem}>
                                        <div className={styles.itemImage}>
                                            <img src={item.anime_title?.poster} alt={item.anime_title?.title} />
                                        </div>
                                        <div className={styles.itemInfo}>
                                            <h3>{item.anime_title?.title}</h3>
                                            <p>Эпизод {item.episode_number} из {item.anime_title?.total_episodes}</p>
                                            <div className={styles.progressBar}>
                                                <div 
                                                    className={styles.progressFill}
                                                    style={{ 
                                                        width: `${getProgressPercent(item.episode_number, item.anime_title?.total_episodes)}%` 
                                                    }}
                                                ></div>
                                            </div>
                                            <span className={styles.watchTime}>
                                                Смотрел {formatDate(item.watch_date)}
                                            </span>
                                        </div>
                                        <div className={styles.itemActions}>
                                            <Button onClick={() => window.open(`/anime/${item.anime_title?.id}`, '_blank')}>
                                                Продолжить
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                {/* История фильмов */}
                                {userHistory.movies.map((item) => (
                                    <div key={item.id} className={styles.historyItem}>
                                        <div className={styles.itemImage}>
                                            <img src={item.movie_title?.poster} alt={item.movie_title?.title} />
                                        </div>
                                        <div className={styles.itemInfo}>
                                            <h3>{item.movie_title?.title}</h3>
                                            <p>Эпизод {item.episode_number} из {item.movie_title?.total_episodes}</p>
                                            <div className={styles.progressBar}>
                                                <div 
                                                    className={styles.progressFill}
                                                    style={{ 
                                                        width: `${getProgressPercent(item.episode_number, item.movie_title?.total_episodes)}%` 
                                                    }}
                                                ></div>
                                            </div>
                                            <span className={styles.watchTime}>
                                                Смотрел {formatDate(item.watch_date)}
                                            </span>
                                        </div>
                                        <div className={styles.itemActions}>
                                            <Button onClick={() => window.open(`/movies/${item.movie_title?.id}`, '_blank')}>
                                                Продолжить
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                {/* Журнал активности */}
                                {userHistory.activity.map((activity) => (
                                    <div key={activity.id} className={styles.activityItem}>
                                        <div className={styles.activityIcon}>
                                            {activity.action_type === 'shop_order' && <ShoppingCartIcon size={18} />}
                                            {activity.action_type === 'food_order' && <PizzaIcon size={18} />}
                                            {activity.action_type === 'it_request' && <ComputerIcon size={18} />}
                                            {activity.action_type === 'chat_message' && <ShoppingCartIcon size={18} />}
                                            {activity.action_type === 'forum_post' && <ShoppingCartIcon size={18} />}
                                            {activity.action_type === 'event_registration' && <PlaneIcon size={18} />}
                                            {activity.action_type === 'media_like' && <StarIcon size={18} />}
                                            {activity.action_type === 'media_comment' && <StarIcon size={18} />}
                                            {activity.action_type === 'media_rating' && <StarIcon size={18} />}
                                        </div>
                                        <div className={styles.activityInfo}>
                                            <h4>{activity.action_type_display}</h4>
                                            <p>{formatDate(activity.created_at)}</p>
                                        </div>
                                        <div className={styles.activityPoints}>
                                            +{activity.points_awarded} очков
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'addresses' && (
                        <div className={styles.addressesSection}>
                            <div className={styles.sectionHeader}>
                                <h3>Адреса доставки</h3>
                                <Button onClick={() => setShowAddressForm(true)}>
                                    Добавить адрес
                                </Button>
                            </div>

                            {showAddressForm && (
                                <div className={styles.addressForm}>
                                    <h4>{editingAddress ? 'Редактировать адрес' : 'Добавить новый адрес'}</h4>
                                    <div className={styles.formGrid}>
                                        <div className={styles.inputGroup}>
                                            <label>Название</label>
                                            <input
                                                type="text"
                                                placeholder="Дом, Офис, и т.д."
                                                value={addressForm.label}
                                                onChange={(e) => setAddressForm({...addressForm, label: e.target.value})}
                                            />
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>Страна</label>
                                            <input
                                                type="text"
                                                value={addressForm.country}
                                                onChange={(e) => setAddressForm({...addressForm, country: e.target.value})}
                                            />
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>Город</label>
                                            <input
                                                type="text"
                                                value={addressForm.city}
                                                onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                                            />
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>Адрес</label>
                                            <input
                                                type="text"
                                                placeholder="Улица, дом, квартира"
                                                value={addressForm.street}
                                                onChange={(e) => setAddressForm({...addressForm, street: e.target.value})}
                                            />
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>Индекс</label>
                                            <input
                                                type="text"
                                                value={addressForm.zipCode}
                                                onChange={(e) => setAddressForm({...addressForm, zipCode: e.target.value})}
                                            />
                                        </div>
                                        <div className={styles.checkboxGroup}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={addressForm.isDefault}
                                                    onChange={(e) => setAddressForm({...addressForm, isDefault: e.target.checked})}
                                                />
                                                Основной адрес
                                            </label>
                                        </div>
                                    </div>
                                    <div className={styles.formActions}>
                                        <button 
                                            className={styles.btnSecondary}
                                            onClick={() => {
                                                setShowAddressForm(false)
                                                setEditingAddress(null)
                                                setAddressForm({
                                                    label: '',
                                                    country: '',
                                                    city: '',
                                                    street: '',
                                                    zipCode: '',
                                                    isDefault: false
                                                })
                                            }}
                                        >
                                            Отмена
                                        </button>
                                        <button 
                                            className={styles.btnPrimary}
                                            onClick={handleAddressSave}
                                        >
                                            {editingAddress ? 'Сохранить' : 'Добавить'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className={styles.addressesList}>
                                {addresses.map((address) => (
                                    <div key={address.id} className={styles.addressItem}>
                                        <div className={styles.addressHeader}>
                                            <h4>{address.label}</h4>
                                            {address.is_default && (
                                                <span className={styles.defaultBadge}>Основной</span>
                                            )}
                                        </div>
                                        <div className={styles.addressDetails}>
                                            <p>{address.country}, {address.city}</p>
                                            <p>{address.street}</p>
                                            {address.zip_code && <p>Индекс: {address.zip_code}</p>}
                                        </div>
                                        <div className={styles.addressActions}>
                                            <button
                                                className={styles.editBtn}
                                                onClick={() => {
                                                    setEditingAddress(address)
                                                    setAddressForm({
                                                        label: address.label,
                                                        country: address.country,
                                                        city: address.city,
                                                        street: address.street,
                                                        zipCode: address.zip_code,
                                                        isDefault: address.is_default
                                                    })
                                                    setShowAddressForm(true)
                                                }}
                                            >
                                                Изменить
                                            </button>
                                            <button
                                                className={styles.deleteBtn}
                                                onClick={() => deleteAddress(address.id)}
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'payment' && (
                        <div className={styles.paymentSection}>
                            <div className={styles.sectionHeader}>
                                <h3>Способы оплаты</h3>
                                <Button onClick={() => setShowPaymentForm(true)}>
                                    Добавить способ оплаты
                                </Button>
                            </div>

                            {showPaymentForm && (
                                <div className={styles.paymentForm}>
                                    <h4>{editingPayment ? 'Редактировать способ оплаты' : 'Добавить новый способ оплаты'}</h4>
                                    <div className={styles.formGrid}>
                                        <div className={styles.inputGroup}>
                                            <label>Тип</label>
                                            <select
                                                value={paymentForm.provider}
                                                onChange={(e) => setPaymentForm({...paymentForm, provider: e.target.value})}
                                            >
                                                <option value="card">Банковская карта</option>
                                                <option value="wallet">Электронный кошелек</option>
                                                <option value="bank">Банковский перевод</option>
                                            </select>
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>Детали</label>
                                            <input
                                                type="text"
                                                placeholder="**** 1234, кошелек@email.com, и т.д."
                                                value={paymentForm.maskedDetails}
                                                onChange={(e) => setPaymentForm({...paymentForm, maskedDetails: e.target.value})}
                                            />
                                        </div>
                                        <div className={styles.checkboxGroup}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={paymentForm.isDefault}
                                                    onChange={(e) => setPaymentForm({...paymentForm, isDefault: e.target.checked})}
                                                />
                                                Основной способ оплаты
                                            </label>
                                        </div>
                                    </div>
                                    <div className={styles.formActions}>
                                        <button 
                                            className={styles.btnSecondary}
                                            onClick={() => {
                                                setShowPaymentForm(false)
                                                setEditingPayment(null)
                                                setPaymentForm({
                                                    provider: 'card',
                                                    maskedDetails: '',
                                                    isDefault: false
                                                })
                                            }}
                                        >
                                            Отмена
                                        </button>
                                        <button 
                                            className={styles.btnPrimary}
                                            onClick={handlePaymentSave}
                                        >
                                            {editingPayment ? 'Сохранить' : 'Добавить'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className={styles.paymentsList}>
                                {paymentMethods.map((payment) => (
                                    <div key={payment.id} className={styles.paymentItem}>
                                        <div className={styles.paymentHeader}>
                                            <div className={styles.paymentType}>
                                                {payment.provider === 'card' && <MoneyIcon size={18} />}
                                                {payment.provider === 'wallet' && <MoneyIcon size={18} />}
                                                {payment.provider === 'bank' && <MoneyIcon size={18} />}
                                                <span>{payment.provider_display}</span>
                                            </div>
                                            {payment.is_default && (
                                                <span className={styles.defaultBadge}>Основной</span>
                                            )}
                                        </div>
                                        <div className={styles.paymentDetails}>
                                            <p>{payment.masked_details}</p>
                                        </div>
                                        <div className={styles.paymentActions}>
                                            <button
                                                className={styles.editBtn}
                                                onClick={() => {
                                                    setEditingPayment(payment)
                                                    setPaymentForm({
                                                        provider: payment.provider,
                                                        maskedDetails: payment.masked_details,
                                                        isDefault: payment.is_default
                                                    })
                                                    setShowPaymentForm(true)
                                                }}
                                            >
                                                Изменить
                                            </button>
                                            <button
                                                className={styles.deleteBtn}
                                                onClick={() => deletePaymentMethod(payment.id)}
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'purchases' && (
                        <div className={styles.purchasesSection}>
                            <div className={styles.sectionHeader}>
                                <h2>Статистика покупок</h2>
                                <Button onClick={loadUserHistory}>Обновить</Button>
                            </div>
                            <div className={styles.ordersStats}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}><ShoppingCartIcon size={32} /></div>
                                    <div className={styles.statInfo}>
                                        <h3>Магазин</h3>
                                        <p className={styles.statValue}>{userHistory.orders?.shop_orders ?? 0}</p>
                                        <span>заказов</span>
                                    </div>
                                </div>
                                
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}><PizzaIcon size={32} /></div>
                                    <div className={styles.statInfo}>
                                        <h3>Еда</h3>
                                        <p className={styles.statValue}>{userHistory.orders?.food_orders ?? 0}</p>
                                        <span>заказов</span>
                                    </div>
                                </div>
                                
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}><ComputerIcon size={32} /></div>
                                    <div className={styles.statInfo}>
                                        <h3>IT услуги</h3>
                                        <p className={styles.statValue}>{userHistory.orders?.it_requests ?? 0}</p>
                                        <span>запросов</span>
                                    </div>
                                </div>
                                
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>📅</div>
                                    <div className={styles.statInfo}>
                                        <h3>События</h3>
                                        <p className={styles.statValue}>{userHistory.orders?.event_registrations ?? 0}</p>
                                        <span>регистраций</span>
                                    </div>
                                </div>
                                
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}><PlaneIcon size={32} /></div>
                                    <div className={styles.statInfo}>
                                        <h3>Туры</h3>
                                        <p className={styles.statValue}>{userHistory.orders?.tour_registrations ?? 0}</p>
                                        <span>регистраций</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className={styles.totalStats}>
                                <h3>Общая статистика</h3>
                                <div className={styles.totalCard}>
                                    <div className={styles.totalValue}>
                                        {(userHistory.orders?.shop_orders ?? 0) + 
                                         (userHistory.orders?.food_orders ?? 0) + 
                                         (userHistory.orders?.it_requests ?? 0) + 
                                         (userHistory.orders?.event_registrations ?? 0) + 
                                         (userHistory.orders?.tour_registrations ?? 0)}
                                    </div>
                                    <span>всего операций</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'shop-orders' && (
                        <div className={styles.shopOrdersSection}>
                            <h2>Заказы магазина</h2>
                            <ShopOrdersList />
                        </div>
                    )}

                    {activeTab === 'food-orders' && (
                        <div className={styles.foodOrdersSection}>
                            <h2>История заказов еды</h2>
                            {foodOrdersLoading ? (
                                <div className={styles.loadingSpinner}>Загрузка заказов...</div>
                            ) : foodOrders.length === 0 ? (
                                <div className={styles.noOrders}>
                                    <div className={styles.noOrdersIcon}><PizzaIcon size={64} /></div>
                                    <h3>Нет заказов</h3>
                                    <p>Вы еще не делали заказы еды</p>
                                </div>
                            ) : (
                                <div className={styles.ordersList}>
                                    {foodOrders.map((order) => (
                                        <div key={order.id} className={styles.orderCard}>
                                            <div className={styles.orderHeader}>
                                                <div className={styles.orderInfo}>
                                                    <h3>Заказ #{order.id}</h3>
                                                    <span className={styles.orderDate}>
                                                        {new Date(order.created_at).toLocaleString('ru-RU', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                                <div className={`${styles.orderStatus} ${styles['status' + order.status]}`}>
                                                    {order.status_display || order.status}
                                                </div>
                                            </div>
                                            
                                            <div className={styles.orderBody}>
                                                <div className={styles.orderDetails}>
                                                    <p><strong>Тип доставки:</strong> {order.delivery_type_display || order.delivery_type}</p>
                                                    <p><strong>Телефон:</strong> {order.phone}</p>
                                                    {order.delivery_address && (
                                                        <p><strong>Адрес:</strong> {order.delivery_address}</p>
                                                    )}
                                                    {order.notes && (
                                                        <p><strong>Примечание:</strong> {order.notes}</p>
                                                    )}
                                                </div>
                                                
                                                {order.items && order.items.length > 0 && (
                                                    <div className={styles.orderItems}>
                                                        <h4>Состав заказа:</h4>
                                                        <ul>
                                                            {order.items.map((item, index) => (
                                                                <li key={index}>
                                                                    {item.food_item?.name || 'Блюдо'} × {item.quantity} - {parseInt(item.unit_price * item.quantity).toLocaleString('ru-RU')} сом
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className={styles.orderFooter}>
                                                <div className={styles.orderTotal}>
                                                    <span>Итого:</span>
                                                    <strong>{parseInt(order.total_amount).toLocaleString('ru-RU')} сом</strong>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'tour-bookings' && (
                        <div className={styles.tourBookingsSection}>
                            <h2>Мои бронирования туров</h2>
                            {tourBookingsLoading ? (
                                <div className={styles.loadingSpinner}>Загрузка бронирований...</div>
                            ) : tourBookings.length === 0 ? (
                                <div className={styles.noOrders}>
                                    <div className={styles.noOrdersIcon}><PlaneIcon size={64} /></div>
                                    <h3>Нет бронирований</h3>
                                    <p>Вы еще не забронировали ни одного тура</p>
                                </div>
                            ) : (
                                <div className={styles.ordersList}>
                                    {tourBookings.map((booking) => (
                                        <div key={booking.id} className={styles.orderCard}>
                                            <div className={styles.orderHeader}>
                                                <div className={styles.orderInfo}>
                                                    <h3>{booking.schedule?.tour?.name || 'Тур'}</h3>
                                                    <span className={styles.orderDate}>
                                                        Забронировано: {new Date(booking.registered_at).toLocaleDateString('ru-RU')}
                                                    </span>
                                                </div>
                                                <div className={`${styles.orderStatus} ${styles['status' + booking.status]}`}>
                                                    {booking.status_display || booking.status}
                                                </div>
                                            </div>
                                            
                                            <div className={styles.orderBody}>
                                                <div className={styles.orderDetails}>
                                                    <p><strong>Дата тура:</strong> {booking.schedule?.date ? new Date(booking.schedule.date).toLocaleDateString('ru-RU') : 'Не указана'}</p>
                                                    <p><strong>Количество участников:</strong> {booking.participants_count}</p>
                                                    <p><strong>Телефон:</strong> {booking.contact_phone}</p>
                                                    <p><strong>Email:</strong> {booking.contact_email}</p>
                                                    {booking.special_requests && (
                                                        <p><strong>Особые пожелания:</strong> {booking.special_requests}</p>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className={styles.orderFooter}>
                                                <div className={styles.orderTotal}>
                                                    <span>Итого:</span>
                                                    <strong>{parseInt(booking.total_price).toLocaleString('ru-RU')} сом</strong>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            <ChangePasswordModal open={openChange} onClose={() => setOpenChange(false)} token={access} />
            <EmailVerificationModal 
                open={openVerification} 
                onClose={() => setOpenVerification(false)} 
                onVerified={() => {
                    console.log('✅ Email verified, refreshing profile');
                    // Обновляем профиль через контекст
                    fetchProfile();
                }}
                userEmail={user?.email}
            />
        </div>
    )
}
